import { CMS_MEDIA_SLOTS } from "@shared/cms";
import { ImagePlus, LayoutPanelTop, LogOut, Pencil, Plus, Save, Trash2, Upload, X } from "lucide-react";
import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";

type DraftPage = {
  id?: number;
  title: string;
  slug: string;
  content: string;
  isPublished: boolean;
};

const emptyPage: DraftPage = { title: "", slug: "", content: "", isPublished: false };

function CmsLogin() {
  const utils = trpc.useUtils();
  const login = trpc.cms.login.useMutation({
    onSuccess: () => void utils.cms.status.invalidate(),
  });
  const [password, setPassword] = useState("");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    login.mutate({ password });
  };

  return (
    <main className="min-h-screen bg-[#111B30] px-4 py-10 text-[#F8F8F6]">
      <div className="mx-auto max-w-md border border-[#F5C400]/30 bg-[#1A2744] p-8 shadow-2xl">
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-[#F5C400]">PPHU Imperium</p>
        <h1 className="mb-3 text-3xl font-extrabold uppercase tracking-tight">Panel CMS</h1>
        <p className="mb-7 text-sm leading-6 text-white/70">Zaloguj się, aby zmieniać opisy, zdjęcia i podstrony witryny.</p>
        <form onSubmit={submit} className="space-y-4">
          <label className="block text-xs font-bold uppercase tracking-[0.12em] text-white/80" htmlFor="cms-password">
            Hasło do panelu
          </label>
          <input
            id="cms-password"
            type="password"
            value={password}
            onChange={event => setPassword(event.target.value)}
            autoComplete="current-password"
            className="w-full border border-white/20 bg-white px-3 py-3 text-[#0D0D0D] outline-none focus:border-[#F5C400]"
            required
          />
          {login.error && <p className="text-sm text-red-300">{login.error.message}</p>}
          <button className="inline-flex w-full items-center justify-center gap-2 bg-[#F5C400] px-4 py-3 text-sm font-extrabold uppercase tracking-[0.08em] text-[#111B30]" disabled={login.isPending} type="submit">
            {login.isPending ? "Logowanie..." : "Zaloguj się"}
          </button>
        </form>
        <Link href="/" className="mt-6 block text-center text-sm text-white/70 underline">Wróć do strony</Link>
      </div>
    </main>
  );
}

export default function Cms() {
  const utils = trpc.useUtils();
  const status = trpc.cms.status.useQuery();
  const authenticated = status.data?.authenticated === true;
  const contentQuery = trpc.cms.content.list.useQuery(undefined, { enabled: authenticated });
  const mediaQuery = trpc.cms.media.list.useQuery(undefined, { enabled: authenticated });
  const pagesQuery = trpc.cms.pages.list.useQuery(undefined, { enabled: authenticated });
  const [tab, setTab] = useState<"content" | "media" | "pages">("content");
  const [contentDrafts, setContentDrafts] = useState<Record<string, string>>({});
  const [pageDraft, setPageDraft] = useState<DraftPage>(emptyPage);
  const [selectedSlot, setSelectedSlot] = useState<(typeof CMS_MEDIA_SLOTS)[number]["slot"]>("home-hero");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [altText, setAltText] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (contentQuery.data) {
      setContentDrafts(Object.fromEntries(contentQuery.data.map(item => [item.contentKey, item.value])));
    }
  }, [contentQuery.data]);

  const saveContent = trpc.cms.content.save.useMutation({
    onSuccess: () => {
      setNotice("Opis został zapisany.");
      void contentQuery.refetch();
      void utils.cms.public.content.invalidate();
    },
  });
  const uploadMedia = trpc.cms.media.upload.useMutation({
    onSuccess: () => {
      setNotice("Zdjęcie zostało zapisane i podmienione.");
      setSelectedFile(null);
      void mediaQuery.refetch();
      void utils.cms.public.media.invalidate();
    },
  });
  const removeMedia = trpc.cms.media.remove.useMutation({
    onSuccess: () => {
      setNotice("Zdjęcie zostało usunięte z wybranego miejsca.");
      void mediaQuery.refetch();
      void utils.cms.public.media.invalidate();
    },
  });
  const savePage = trpc.cms.pages.save.useMutation({
    onSuccess: () => {
      setNotice("Podstrona została zapisana.");
      setPageDraft(emptyPage);
      void pagesQuery.refetch();
      void utils.cms.public.pages.invalidate();
    },
  });
  const removePage = trpc.cms.pages.remove.useMutation({
    onSuccess: () => {
      setNotice("Podstrona została usunięta.");
      setPageDraft(emptyPage);
      void pagesQuery.refetch();
      void utils.cms.public.pages.invalidate();
    },
  });
  const logout = trpc.cms.logout.useMutation({
    onSuccess: () => void utils.cms.status.invalidate(),
  });

  const mediaBySlot = useMemo(
    () => new Map((mediaQuery.data ?? []).map(media => [media.slot, media])),
    [mediaQuery.data]
  );

  if (status.isLoading) {
    return <main className="min-h-screen bg-[#111B30] p-8 text-white">Sprawdzanie dostępu do panelu...</main>;
  }
  if (!authenticated) return <CmsLogin />;

  const slotConfig = CMS_MEDIA_SLOTS.find(item => item.slot === selectedSlot) ?? CMS_MEDIA_SLOTS[0];

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size > 6 * 1024 * 1024) {
      setNotice("Wybierz zdjęcie JPG, PNG lub WEBP o maksymalnym rozmiarze 6 MB.");
      event.target.value = "";
      return;
    }
    setSelectedFile(file);
    setAltText(slotConfig.altText);
  };

  const uploadSelectedFile = () => {
    if (!selectedFile) return;
    const reader = new FileReader();
    reader.onload = () => uploadMedia.mutate({
      slot: selectedSlot,
      label: slotConfig.label,
      altText: altText.trim() || slotConfig.altText,
      dataUrl: String(reader.result),
    });
    reader.readAsDataURL(selectedFile);
  };

  return (
    <main className="min-h-screen bg-[#F8F8F6] text-[#0D0D0D]">
      <header className="border-b-4 border-[#F5C400] bg-[#111B30] text-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-5 sm:px-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#F5C400]">PPHU Imperium</p>
            <h1 className="text-2xl font-extrabold uppercase">Panel CMS</h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <a className="border border-white/25 px-3 py-2 text-xs font-bold uppercase tracking-[0.06em]" href="/" target="_blank" rel="noreferrer">Zobacz stronę</a>
            <button className="inline-flex items-center gap-2 border border-white/25 px-3 py-2 text-xs font-bold uppercase tracking-[0.06em]" onClick={() => logout.mutate()} type="button"><LogOut size={15} /> Wyloguj</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex flex-wrap gap-2 border-b border-[#D1D5DB] pb-3">
          <button className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-bold ${tab === "content" ? "bg-[#1A2744] text-white" : "bg-white"}`} onClick={() => setTab("content")} type="button"><Pencil size={16} /> Opisy</button>
          <button className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-bold ${tab === "media" ? "bg-[#1A2744] text-white" : "bg-white"}`} onClick={() => setTab("media")} type="button"><ImagePlus size={16} /> Zdjęcia</button>
          <button className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-bold ${tab === "pages" ? "bg-[#1A2744] text-white" : "bg-white"}`} onClick={() => setTab("pages")} type="button"><LayoutPanelTop size={16} /> Podstrony</button>
        </div>

        {notice && <div className="mb-5 flex items-center justify-between border-l-4 border-[#F5C400] bg-[#FFF9E0] px-4 py-3 text-sm"><span>{notice}</span><button onClick={() => setNotice("")} aria-label="Zamknij komunikat"><X size={17} /></button></div>}

        {tab === "content" && (
          <section>
            <div className="mb-6"><h2 className="text-2xl font-extrabold uppercase">Edytuj opisy</h2><p className="mt-1 text-sm text-[#6B7280]">Zmień tekst i zapisz. Nowa treść pojawi się na stronie publicznej.</p></div>
            <div className="grid gap-5 lg:grid-cols-2">
              {(contentQuery.data ?? []).map(field => (
                <article key={field.contentKey} className="border border-[#E5E7EB] bg-white p-5 shadow-sm">
                  <label className="mb-2 block text-sm font-bold" htmlFor={field.contentKey}>{field.label}</label>
                  <textarea id={field.contentKey} value={contentDrafts[field.contentKey] ?? ""} onChange={event => setContentDrafts(current => ({ ...current, [field.contentKey]: event.target.value }))} rows={5} className="w-full border border-[#D1D5DB] p-3 text-sm leading-6 outline-none focus:border-[#F5C400]" />
                  <button className="mt-3 inline-flex items-center gap-2 bg-[#F5C400] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.06em] text-[#111B30]" onClick={() => saveContent.mutate({ contentKey: field.contentKey, label: field.label, value: contentDrafts[field.contentKey] ?? field.defaultValue })} disabled={saveContent.isPending} type="button"><Save size={15} /> Zapisz opis</button>
                </article>
              ))}
            </div>
          </section>
        )}

        {tab === "media" && (
          <section>
            <div className="mb-6"><h2 className="text-2xl font-extrabold uppercase">Zdjęcia</h2><p className="mt-1 text-sm text-[#6B7280]">Wybierz miejsce na stronie, a potem dodaj albo podmień zdjęcie. Dozwolone są JPG, PNG i WEBP do 6 MB.</p></div>
            <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="border border-[#E5E7EB] bg-white p-5">
                <h3 className="mb-3 font-bold">Wybierz miejsce</h3>
                <div className="space-y-2">
                  {CMS_MEDIA_SLOTS.map(item => {
                    const current = mediaBySlot.get(item.slot);
                    return <button key={item.slot} type="button" onClick={() => { setSelectedSlot(item.slot); setSelectedFile(null); setAltText(item.altText); }} className={`flex w-full items-center justify-between gap-3 border p-3 text-left text-sm ${selectedSlot === item.slot ? "border-[#F5C400] bg-[#FFF9E0]" : "border-[#E5E7EB] bg-white"}`}><span>{item.label}</span><span className="text-xs text-[#6B7280]">{current ? "zdjęcie dodane" : "brak zmian"}</span></button>;
                  })}
                </div>
              </div>
              <div className="border border-[#E5E7EB] bg-white p-5">
                <h3 className="mb-1 text-lg font-bold">{slotConfig.label}</h3>
                <p className="mb-4 text-sm text-[#6B7280]">Opis alternatywny pomaga osobom korzystającym z czytników ekranu.</p>
                {mediaBySlot.get(selectedSlot) && <img className="mb-4 max-h-72 w-full object-cover" src={mediaBySlot.get(selectedSlot)?.url} alt={mediaBySlot.get(selectedSlot)?.altText} />}
                <label className="mb-3 flex cursor-pointer items-center justify-center gap-2 border-2 border-dashed border-[#D1D5DB] p-6 text-sm font-bold"><Upload size={18} /> {selectedFile ? selectedFile.name : "Wybierz zdjęcie"}<input className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFile} /></label>
                <label className="mb-3 block text-sm font-bold" htmlFor="alt-text">Opis zdjęcia</label>
                <input id="alt-text" value={altText} onChange={event => setAltText(event.target.value)} className="mb-4 w-full border border-[#D1D5DB] p-3 text-sm outline-none focus:border-[#F5C400]" />
                <div className="flex flex-wrap gap-3">
                  <button className="inline-flex items-center gap-2 bg-[#F5C400] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.06em] text-[#111B30]" onClick={uploadSelectedFile} disabled={!selectedFile || uploadMedia.isPending} type="button"><Upload size={15} /> {uploadMedia.isPending ? "Zapisywanie..." : "Zapisz zdjęcie"}</button>
                  {mediaBySlot.get(selectedSlot) && <button className="inline-flex items-center gap-2 border border-[#C41E1E] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.06em] text-[#C41E1E]" onClick={() => removeMedia.mutate({ id: mediaBySlot.get(selectedSlot)!.id })} disabled={removeMedia.isPending} type="button"><Trash2 size={15} /> Usuń zmianę</button>}
                </div>
              </div>
            </div>
          </section>
        )}

        {tab === "pages" && (
          <section>
            <div className="mb-6 flex flex-wrap items-end justify-between gap-3"><div><h2 className="text-2xl font-extrabold uppercase">Podstrony</h2><p className="mt-1 text-sm text-[#6B7280]">Utwórz własną podstronę, zapisz szkic lub od razu ją opublikuj.</p></div><button className="inline-flex items-center gap-2 bg-[#1A2744] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.06em] text-white" onClick={() => setPageDraft(emptyPage)} type="button"><Plus size={15} /> Nowa podstrona</button></div>
            <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
              <form className="border border-[#E5E7EB] bg-white p-5" onSubmit={event => { event.preventDefault(); savePage.mutate(pageDraft); }}>
                <h3 className="mb-4 text-lg font-bold">{pageDraft.id ? "Edytuj podstronę" : "Nowa podstrona"}</h3>
                <label className="mb-2 block text-sm font-bold" htmlFor="page-title">Tytuł</label><input id="page-title" value={pageDraft.title} onChange={event => setPageDraft(current => ({ ...current, title: event.target.value }))} className="mb-4 w-full border border-[#D1D5DB] p-3 text-sm outline-none focus:border-[#F5C400]" required />
                <label className="mb-2 block text-sm font-bold" htmlFor="page-slug">Adres podstrony</label><div className="mb-4 flex border border-[#D1D5DB]"><span className="bg-[#F8F8F6] px-3 py-3 text-sm text-[#6B7280]">/strona/</span><input id="page-slug" value={pageDraft.slug} onChange={event => setPageDraft(current => ({ ...current, slug: event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") }))} className="min-w-0 flex-1 p-3 text-sm outline-none focus:bg-[#FFF9E0]" placeholder="np-o-firmie" required /></div>
                <label className="mb-2 block text-sm font-bold" htmlFor="page-content">Treść</label><textarea id="page-content" value={pageDraft.content} onChange={event => setPageDraft(current => ({ ...current, content: event.target.value }))} rows={12} className="mb-4 w-full border border-[#D1D5DB] p-3 text-sm leading-6 outline-none focus:border-[#F5C400]" placeholder="Każdy nowy akapit wpisz w osobnej linii." required />
                <label className="mb-5 flex items-center gap-2 text-sm font-bold"><input type="checkbox" checked={pageDraft.isPublished} onChange={event => setPageDraft(current => ({ ...current, isPublished: event.target.checked }))} /> Opublikuj od razu</label>
                <button className="inline-flex items-center gap-2 bg-[#F5C400] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.06em] text-[#111B30]" disabled={savePage.isPending} type="submit"><Save size={15} /> {savePage.isPending ? "Zapisywanie..." : "Zapisz podstronę"}</button>
              </form>
              <div className="border border-[#E5E7EB] bg-white p-5"><h3 className="mb-4 text-lg font-bold">Utworzone podstrony</h3><div className="space-y-3">{(pagesQuery.data ?? []).length === 0 && <p className="text-sm text-[#6B7280]">Nie utworzono jeszcze żadnej podstrony.</p>}{(pagesQuery.data ?? []).map(page => <article key={page.id} className="border border-[#E5E7EB] p-4"><div className="flex items-start justify-between gap-3"><div><h4 className="font-bold">{page.title}</h4><p className="mt-1 text-xs text-[#6B7280]">/strona/{page.slug} · {page.isPublished ? "opublikowana" : "szkic"}</p></div><span className={`px-2 py-1 text-xs font-bold ${page.isPublished ? "bg-green-100 text-green-800" : "bg-[#F8F8F6] text-[#6B7280]"}`}>{page.isPublished ? "ONLINE" : "SZKIC"}</span></div><div className="mt-4 flex gap-3"><button className="text-xs font-bold uppercase text-[#1A2744]" onClick={() => setPageDraft({ id: page.id, title: page.title, slug: page.slug, content: page.content, isPublished: page.isPublished })} type="button">Edytuj</button><button className="text-xs font-bold uppercase text-[#C41E1E]" onClick={() => { if (window.confirm(`Usunąć podstronę „${page.title}”?`)) removePage.mutate({ id: page.id }); }} type="button">Usuń</button></div></article>)}</div></div>
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
